import detectIncognito from 'detectincognitojs';
import type { FirebaseApp } from 'firebase/app';
import { initializeApp } from 'firebase/app';
import { deleteToken, getMessaging, getToken, isSupported, type Messaging } from 'firebase/messaging';
import { resolveURL } from 'ufo';
import { setFcmDeviceToken, setIsPrivate } from '@/stores/device';
import { BASE_URL } from '@/utils/constants';
import { createLogger } from '@/utils/logger';

export function createFirebaseManager() {
  return new FirebaseManager();
}

export default class FirebaseManager {
  readonly #instance: FirebaseApp;
  readonly #logger = createLogger('Firebase');
  #messagingWorker: ServiceWorkerRegistration | null = null;

  #isInitialized = false;

  constructor() {
    this.#instance = initializeApp(window.__CONFIG?.config ?? {});
  }

  get isInitialized() {
    return this.#isInitialized;
  }

  get messagingInstance(): Messaging | undefined {
    if (this.#instance) {
      return getMessaging(this.#instance);
    }

    return undefined;
  }

  async revokeDeviceToken() {
    if (this.#instance) {
      await deleteToken(getMessaging(this.#instance));
    }
  }

  async shouldRequestPermission(permission: NotificationPermission = 'default') {
    if (!(await isSupported())) {
      this.#logger.warn('Notifications is not supported.');
      return false;
    }

    const { isPrivate } = await detectIncognito();
    setIsPrivate(isPrivate);

    if (isPrivate) {
      this.#logger.warn('Notifications are not supported in private or incognito mode.');
      return false;
    }

    const currentPermission = Notification.permission || (await Notification.requestPermission());
    if (currentPermission === permission) {
      return true;
    }
    this.#logger.info(`Notification permission "${currentPermission}" does not match the requested "${permission}".`);

    return false;
  }

  async initialize() {
    this.#messagingWorker = await navigator.serviceWorker.register(resolveURL(BASE_URL, 'fcm-sw.js'), {
      scope: '/firebase-cloud-messaging-push-scope',
      type: 'module',
    });

    await this.#messagingWorker.update();

    navigator.serviceWorker.addEventListener('message', (e) => {
      const { type, payload } = e.data;

      switch (type) {
        case 'navigate':
          if (window.location.href !== payload) {
            window.location.href = payload;
          }
          break;
      }
    });

    const token = await this.#initializeDeviceToken();

    this.#isInitialized = true;

    return token;
  }

  async #initializeDeviceToken() {
    const vapidKey = window.__CONFIG?.vapId;
    let token = '';

    if (!vapidKey) {
      this.#logger.warn('No vapid key provided.');
      return token;
    }

    try {
      if (this.#messagingWorker) {
        token = await getToken(getMessaging(this.#instance), {
          vapidKey,
          serviceWorkerRegistration: this.#messagingWorker,
        });
      }

      if (token) {
        this.#logger.info('retrieve token successful.');
        setFcmDeviceToken(token);
      } else {
        this.#logger.warn('No registration token available. Request permission to generate one.');
      }
    } catch (e) {
      this.#logger.error('An error occurred while retrieving token.', e);
    }

    return token;
  }
}
